import React, { useMemo } from "react";
import { useTranslation } from "react-i18next"
import { Controller } from "react-hook-form"
import FormSimple from "@components/formGroup/formSimple"
import { Input } from "@components/tailwindComponents"
import ListboxSimple from "@components/tailwindComponents/ListboxSimple"
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker"
import InputWithAddOptions from "@components/input/InputWithAddOptions"
import useGenderType from "@pages/shared/employeFormNew/PersonnalInformations/hooksGenderType"
import COUNTRY_LIST from "@pages/localStorage/devise"
import { GENDER_LIST, LANGUAGE_LIST } from "@pages/shared/employeFormNew/employeForm-constants"

export const useSectionPersonnalInfoColumns = () => {
  const { t } = useTranslation("pages.employés.infoPers")

  return useMemo(() => [
    { id: "legalFirstName", label: t("Prénom légal*") },
    { id: "legalName", label: t("Nom légal*") },
    { id: "legalGender", label: t("Genre légal*") },
    { id: "dateOfBirth", label: t("Date de naissance") },
    { id: "firstNameUsed", label: t("Prénom utilisé") },
    { id: "nameUsed", label: t("Nom utilisé") },
    { id: "originCountry", label: t("Pays d'origine") },
    { id: "spokenLanguages", label: t("Langues parlées") }
  ], [t])
}

export const SectionPersonnalInfoTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers")

  return (
    <>
      <th
        key="legalFirstName"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.legalFirstName ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Prénom légal*")}
      </th>
      <th
        key="legalName"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.legalName ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Nom légal*")}
      </th>
      <th
        key="legalGender"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.legalGender ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Genre légal*")}
      </th>
      <th
        key="dateOfBirth"
        className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.dateOfBirth ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Date de naissance")}
      </th>
      <th
        key="firstNameUsed"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.firstNameUsed ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Prénom utilisé")}
      </th>
      <th
        key="nameUsed"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.nameUsed ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Nom utilisé")}
      </th>
      <th
        key="originCountry"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.originCountry ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Pays d'origine")}
      </th>
      <th
        key="spokenLanguages"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.spokenLanguages ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Langues parlées")}
      </th>
    </>
  )
}

export const SectionPersonnalInfoTableBulkCells = ({ control, employeeIndex, employee, readOnly = false, mustWait, hiddenColumns = {} }) => {
  const { t: countryTranslator } = useTranslation("components.dropdown.dropdownCountry")
  const { t: languageTranslator } = useTranslation("pages.employés.statistics")
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers")
  
  const { allGenderTypes, addGenderType, updateGenderType, deleteGenderType } = useGenderType()

  const genderOptions = useMemo(() => {
    return allGenderTypes.map(gender => ({
      ...gender,
      title: gender._id,
      showAction: !GENDER_LIST.some(g => g._id === gender._id)
    }));
  }, [allGenderTypes])

  return (
    <>
      <td
        key="legalFirstName"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.legalFirstName ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.legalFirstName`}
          defaultValue={employee.personnalInformation?.legalFirstName || ""}
          rules={{ required: !readOnly }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <FormSimple
              placeholder={mainTranslator("Prénom légal*")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 truncate whitespace-nowrap overflow-hidden"
              edit={true}
              invalid={invalid}
            />
          )}
        />
      </td>
      <td
        key="legalName"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.legalName ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.legalName`}
          defaultValue={employee.personnalInformation?.legalName || ""}
          rules={{ required: !readOnly }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <FormSimple
              placeholder={mainTranslator("Nom légal*")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 truncate whitespace-nowrap overflow-hidden"
              edit={true}
              invalid={invalid}
            />
          )}
        />
      </td>
      <td
        key="legalGender"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.legalGender ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.legalGender`}
          defaultValue={employee.personnalInformation?.legalGender || ""}
          rules={{ required: !readOnly }}
          render={({ field: { onChange, value, name }, fieldState: { invalid } }) => (
            <div className="arh-flex arh-flex-col">
              <InputWithAddOptions
                name={name}
                defaultText={mainTranslator("Genre légal*")}
                optionsList={genderOptions.map(option => ({
                  ...option,
                  showAction: !GENDER_LIST.some(g => g._id === option._id)
                }))}
                secondDefaultText={mainTranslator("Ajouter")}
                value={value}
                optionListLabel="name"
                onChange={(event) => {
                  if (event?.target?.dataset?.value) {
                    onChange(genderOptions.find(g => g.name === event.target.dataset.value).title);
                  }
                }}
                addItem={addGenderType}
                editItem={updateGenderType}
                deleteItem={deleteGenderType}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                validateDropdown={invalid}
                showAction
                disabled={mustWait || readOnly}
                classBtn="arh-absolute arh-right-1 arh-top-1/2 -arh-translate-y-1/2 !arh-py-[0.0625rem] !arh-px-2.5"
              />
            </div>
          )}
        />
      </td>
      <td
        key="dateOfBirth"
        className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.dateOfBirth ? "arh-hidden" : ""}`}
      >
        <div className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-flex arh-w-full">
          {mustWait || readOnly ? (
            <Input
              value={employee?.personnalInformation?.dateOfBirth || ""}
              placeholder={mainTranslator("Date de naissance")}
              disabled
              additionnalClas="!arh-py-[5px] !arh-px-2.5"
            />
          ) : (
            <Controller
              control={control}
              name={`employees.${employeeIndex}.personnalInformation.dateOfBirth`}
              defaultValue={employee.personnalInformation?.dateOfBirth || ""}
              render={({ field: { onChange, value } }) => (
                <DefaultDatePicker
                  placeholder={mainTranslator("Date de naissance")}
                  dateValue={value}
                  setDateValue={onChange}
                  className="focus_green dateOfBirth"
                  inputDatePicker="form-control !arh-h-[calc(2rem+2px)] !arh-py-[5px] !arh-px-2.5"
                />
              )}
            />
          )}
        </div>
      </td>
      <td
        key="firstNameUsed"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.firstNameUsed ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.firstNameUsed`}
          defaultValue={employee.personnalInformation?.firstNameUsed || ""}
          render={({ field: { onChange, value } }) => (
            <FormSimple
              placeholder={mainTranslator("Prénom utilisé")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 truncate whitespace-nowrap overflow-hidden"
              edit={true}
            />
          )}
        />
      </td>
      <td
        key="nameUsed"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.nameUsed ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.nameUsed`} 
          defaultValue={employee.personnalInformation?.nameUsed || ""}
          render={({ field: { onChange, value } }) => (
            <FormSimple
              placeholder={mainTranslator("Nom utilisé")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 truncate whitespace-nowrap overflow-hidden"
              edit={true}
            />
          )}
        />
      </td>
      <td
        key="originCountry"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.originCountry ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.originCountry`}
          defaultValue={employee.personnalInformation?.originCountry || ""}
          render={({ field: { onChange, value, name } }) => {
            const selectedCountry = COUNTRY_LIST.find(c => c.pays === value);
            return (
              <ListboxSimple
                name={name}
                placeholder={mainTranslator("Pays d'origine")}
                optionList={COUNTRY_LIST}
                optionValueAttibut="pays"
                optionTextAttribut="pays"
                selectedValue={selectedCountry}
                setSelectedValue={(val) => onChange(val?.pays || "")}
                readOnly={mustWait || readOnly}
                classParentList="arh-mb-0"
                customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 truncate whitespace-nowrap overflow-hidden"
                searchInputClass="!arh-py-[5px] !arh-px-2.5"
                withSearch
                customTranslator={countryTranslator}
              />
            );
          }}
        />
      </td>
      <td
        key="spokenLanguages"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.spokenLanguages ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.personnalInformation.spokenLanguages`}
          defaultValue={employee.personnalInformation?.spokenLanguages || []}
          render={({ field: { onChange, value, name } }) => {
            const selectedLanguages = LANGUAGE_LIST.filter(l =>
              (value || []).includes(l._id)
            );
            return (
              <ListboxSimple
                name={name}
                placeholder={mainTranslator("Langues parlées")}
                optionList={LANGUAGE_LIST}
                selectedValue={selectedLanguages}
                setSelectedValue={(vals) => onChange(vals.map(v => v._id))}
                readOnly={mustWait || readOnly}
                classParentList="arh-mb-0"
                customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 truncate whitespace-nowrap overflow-hidden "
                searchInputClass="!arh-py-[5px] !arh-px-2.5"
                multiple
                withSearch
                customTranslator={languageTranslator}
              />
            );
          }}
        />
      </td>
    </>
  )
}

export const getPersonnalInfoColumnCount = () => 8